import * as React from 'react';

import {
  getThemeSnapshot,
  initThemeStore,
  setThemeMode,
  subscribeTheme,
  toggleThemeMode,
  type ResolvedThemeMode,
  type ThemeMode,
} from '../theme/theme-store';

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedMode: ResolvedThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: ThemeMode;
}

// Provider now only initializes the store; it does NOT hold React state.
// This prevents a global rerender on theme changes.
export function ThemeProvider({ children, defaultMode }: ThemeProviderProps) {
  React.useEffect(() => {
    initThemeStore(defaultMode ?? 'dark');
  }, [defaultMode]);

  return <>{children}</>;
}

export function useThemeMode(): ThemeContextValue {
  const snap = React.useSyncExternalStore(subscribeTheme, getThemeSnapshot, getThemeSnapshot);

  const setMode = React.useCallback((mode: ThemeMode) => {
    setThemeMode(mode);
  }, []);

  const toggleMode = React.useCallback(() => {
    toggleThemeMode();
  }, []);

  return {
    mode: snap.mode,
    resolvedMode: snap.resolvedMode,
    setMode,
    toggleMode,
  };
}
